import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminGetAccountByEmailParams,
  AdminGetAccountByPhoneParams,
  AdminGetAccountParams,
  AdminGetMissionsParams,
  AdminGetUserByEmailParams,
  AdminGetUserByPhoneParams,
  AdminGetUserOtcHistoryParams,
  AdminGetUserParams,
  AdminGetUserPortfolioParams,
  AdminListAccountsParams,
  AdminListUserBankAccountsParams,
  AdminCreateAccountInput,
  AdminSetEmailInput,
  AdminSetPhoneInput,
  AdminToggleAccountActiveInput,
} from '../api'
import type { IMutationArgs } from '../types'
import {
  adminCreateAccountOptions,
  adminGetAccountByEmailOptions,
  adminGetAccountByPhoneOptions,
  adminGetAccountOptions,
  adminGetMissionsOptions,
  adminGetUserByEmailOptions,
  adminGetUserByIdOptions,
  adminGetUserByPhoneOptions,
  adminGetUserOptions,
  adminGetUserOtcHistoryOptions,
  adminGetUserPortfolioOptions,
  adminListAccountsOptions,
  adminListUserBankAccountsOptions,
  adminToggleAccountActiveOptions,
} from './admin-account.options'
import adminAccountService from './admin-account.service'
import { EAdminAccountQueryKey } from './admin-account.types'

export const useAdminGetAccount = (params: AdminGetAccountParams) => {
  return useQuery(adminGetAccountOptions(params))
}

export const useAdminListAccounts = (params: AdminListAccountsParams) => {
  return useQuery(adminListAccountsOptions(params))
}

export const useAdminGetAccountByEmail = (
  params: AdminGetAccountByEmailParams,
) => {
  return useQuery(adminGetAccountByEmailOptions(params))
}

export const useAdminGetAccountByPhone = (
  params: AdminGetAccountByPhoneParams,
) => {
  return useQuery(adminGetAccountByPhoneOptions(params))
}

export const useAdminGetUser = (params: AdminGetUserParams) => {
  return useQuery(adminGetUserOptions(params))
}

export const useAdminCreateAccount = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (data: AdminCreateAccountInput) =>
      adminCreateAccountOptions(data).mutationFn(data),
    mutationKey: [EAdminAccountQueryKey.ADMIN_ACCOUNTS],
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_ACCOUNTS],
        })
      return data
    },
  })
}

export const useAdminToggleAccountActive = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (data: AdminToggleAccountActiveInput) =>
      adminToggleAccountActiveOptions(data).mutationFn(data),
    mutationKey: [EAdminAccountQueryKey.ADMIN_ACCOUNT],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_ACCOUNT],
        })
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_ACCOUNTS],
        })
      }
      return data
    },
  })
}

export const useAdminAddEmailToAccount = () => {
  return useMutation({
    mutationFn: (input: AdminSetEmailInput) =>
      adminAccountService.addEmailToAccount(input),
  })
}

export const useAdminAddPhoneToAccount = () => {
  return useMutation({
    mutationFn: (input: AdminSetPhoneInput) =>
      adminAccountService.addPhoneToAccount(input),
  })
}

export const useAdminGetUserByEmail = (params: AdminGetUserByEmailParams) => {
  return useQuery(adminGetUserByEmailOptions(params))
}

export const useAdminGetUserByPhone = (params: AdminGetUserByPhoneParams) => {
  return useQuery(adminGetUserByPhoneOptions(params))
}

export const useAdminGetUserById = (params: AdminGetUserParams) => {
  return useQuery(adminGetUserByIdOptions(params))
}

export const useAdminGetMissions = (params: AdminGetMissionsParams) => {
  return useQuery(adminGetMissionsOptions(params))
}

export const useAdminGetUserPortfolio = (
  params: AdminGetUserPortfolioParams,
) => {
  return useQuery(adminGetUserPortfolioOptions(params))
}

export const useAdminGetUserOtcHistory = (
  params: AdminGetUserOtcHistoryParams,
) => {
  return useQuery(adminGetUserOtcHistoryOptions(params))
}

export const useAdminDeleteUser = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (id: number) => adminAccountService.adminDeleteUser(id),
    mutationKey: [EAdminAccountQueryKey.ADMIN_USER],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_USER],
        })
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_USER_BY_ID],
        })
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_USER_BY_EMAIL],
        })
        queryClient.invalidateQueries({
          queryKey: [EAdminAccountQueryKey.ADMIN_USER_BY_PHONE],
        })
      }
      return data
    },
  })
}

export const useAdminListUserBankAccounts = (
  params: AdminListUserBankAccountsParams,
) => {
  return useQuery(adminListUserBankAccountsOptions(params))
}
